/* eslint-disable lingui/no-unlocalized-strings -- contratos técnicos HTTP */
import { creditApi, searchApi } from '@/shared/http/client';
import { wire, withSignal } from './wire';
import {
  auditListSchema,
  cosifAccountListSchema,
  enumValueListSchema,
  tenantOptionListSchema,
  bankOptionPageSchema,
  signedUploadSchema,
  type AuditEntry,
} from './reference.wire';
function parseValues(
  json: string | null | undefined,
  legacy: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (json) {
    try {
      const parsed: unknown = JSON.parse(json);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
        return parsed as Record<string, unknown>;
    } catch {
      // igual ao legado: JSON quebrado cai no objeto antigo
    }
  }
  return legacy ?? {};
}
export function normalizeAuditEntry(entry: AuditEntry): AuditEntry {
  return {
    ...entry,
    currentValues: parseValues(entry.currentValuesJson, entry.currentValues),
    previousValues: parseValues(entry.previousValuesJson, entry.previousValues),
    additionalValues: parseValues(entry.additionalValuesJson, entry.additionalValues),
  };
}
export async function getAudit(recordType: string, id: string, signal?: AbortSignal) {
  const data = await searchApi.get<unknown>(`/audit/${recordType}/${id}`, {
    params: { size: 10 },
    ...withSignal(signal),
  });
  return wire(auditListSchema, data ?? [], `GET /audit/${recordType}/{id}`).map(
    normalizeAuditEntry,
  );
}
export async function getEnumValues(
  name: string,
  options: { size: number; search?: string | undefined },
  signal?: AbortSignal,
) {
  const params: Record<string, unknown> = { size: options.size };
  if (options.search && options.search.length >= 3) params['search'] = options.search;
  const data = await creditApi.get<unknown>(`/Enum/${name}`, { params, ...withSignal(signal) });
  return wire(enumValueListSchema, data, `GET /Enum/${name}`);
}
export async function listTenantOptions(signal?: AbortSignal) {
  const data = await creditApi.get<unknown>('/Tenant', {
    params: { searchString: undefined, page: 0, size: 10 },
    ...withSignal(signal),
  });
  return wire(tenantOptionListSchema, data, 'GET /Tenant');
}
/** `getBankList` (services/bankList/bankList.ts:4-30); `compe` padrão `true` (o `useBankList({ compe })` passa `false` no Pix). */
export async function listBankOptions(search: string | undefined, signal?: AbortSignal, compe = true) {
  const isCode = !!search && /^[0-9]+$/.test(search);
  const isName = !isCode && typeof search === 'string' && search.replace(/\s/g, '') !== '';
  const params = {
    name: isName ? search : undefined,
    code: isCode ? search : undefined,
    compe,
  };
  const data = await creditApi.get<unknown>('/Bank', { params, ...withSignal(signal) });
  return wire(bankOptionPageSchema, data, 'GET /Bank');
}
export async function getSignedUploadUrl(fileName: string, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/Upload/${encodeURI(fileName)}`, withSignal(signal));
  return wire(signedUploadSchema, data, 'GET /Upload/{fileName}');
}
export async function putFileToSignedUrl(
  file: Blob,
  putUrl: string,
  options: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
) {
  const fileName = new URL(putUrl).pathname.split('/').reverse()[0] ?? '';
  const { onProgress } = options;
  await creditApi.put(putUrl, file, {
    timeout: 0,
    authorization: 'none',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'Content-Disposition': `inline; filename="${fileName}"`,
    },
    ...withSignal(options.signal),
    // Progresso do envio para o `DocumentsField` (P1); não muda o fio.
    ...(onProgress
      ? {
          onUploadProgress: (event: { loaded: number; total?: number | undefined }) =>
            event.total ? onProgress(event.loaded / event.total) : undefined,
        }
      : {}),
  });
}

/** UYZY services/creditNote/creditNote.ts:515: contas de transferência, sem tenant. */
export async function listCosifAccounts(signal?: AbortSignal) {
  return wire(
    cosifAccountListSchema,
    await creditApi.get<unknown>('/CosifAccount', {
      params: { transactionType: 'Transfer' },
      ...withSignal(signal),
    }),
    'GET /CosifAccount',
  );
}
